import React, { useState } from "react";
import "./suggestion-card.css";
import { FiMinimize2 } from "react-icons/fi";
import { BsXCircleFill, BsFillCheckCircleFill } from "react-icons/bs";
import PropTypes from "prop-types";
import { rejectSuggestion, replaceWord, handleHighlight, handleRemoveHighlight, unsetTextSelection, toggleEditorUpdate} from "./mechanism";
import { useTranslation, initReactI18next } from "react-i18next";
import i18n from "i18next";
import translationEN from "../../../constants/locales/en/translation.json";
import translationID from "../../../constants/locales/id/translation.json";
import { FaSpinner } from 'react-icons/fa';

/**
 * SuggestionCard component displays a single word suggestion.
 *
 * @param {{
 *  word: {originalWord: string, replacement: string, offset: number, deleteCount: number},
 *  editorRef:  import('react').MutableRefObject<import('@tiptap/react').Editor>,
 *  groupWords: {sameWords: array[word],}
 *  onRemove: Function,
 * }} props The properties passed to the component.
 * - originalWord: The original word to be replaced
 * - replacement: The word that will replace the original word
 * - offset: The position in the text where the replacement starts
 * - deleteCount: The number of characters to be replaced in the original text
 */

const resources = {
  en: {
    translation: translationEN,
  },
  id: {
    translation: translationID,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: "en",
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const SuggestionCard = ({ word, editorRef, groupWords, onRemove, onCardClick, highlighted, onClearHighlight, webSocket, isLoading, setIsLoading}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  const expandCard = (e) => {
    e.stopPropagation();
    setIsExpanded(true);
    handleHighlightWord();
    onCardClick();
  };

  const handleHighlightWord = () => {
    // istanbul ignore next
    if(highlighted) {
      handleRemoveHighlights();
    }
    const key = word.originalWord.toLowerCase() + " " + word.errorType;
    const offset = word.offset + 1;
    for (const sameWord of groupWords[key].sameWords) {
      if (sameWord.offset + 1 !== offset) {
        handleHighlight(
          editorRef.current,
          sameWord.offset + 1,
          sameWord.offset + 1 + sameWord.deleteCount
        );
      }
    };
    handleHighlight(
      editorRef.current,
      word.offset +1,
      word.offset + 1+ word.deleteCount
    );
  };

  const handleRemoveHighlights = (options) => {
    const removeTextSelection = options?.removeTextSelection ?? false;
    // istanbul ignore next
    const key = highlighted ? highlighted.originalWord.toLowerCase() + " " + highlighted.errorType : word.originalWord.toLowerCase() + " " + word.errorType;
    for (const sameWord of groupWords[key].sameWords) {
      handleRemoveHighlight(
        editorRef.current,
        sameWord.offset + 1,
        sameWord.offset + 1 + sameWord.deleteCount
      );
    };
    handleRemoveHighlight(
      editorRef.current,
      word.offset + 1,
      word.offset + 1 + word.deleteCount
    );

    if (removeTextSelection) {
      unsetTextSelection(editorRef.current);
      unsetTextSelection(
        editorRef.current,
        word.offset + 1,
        word.offset + 1 + word.deleteCount,
        word.errorType
      );
    }
  };

  const minimizeCard = (e) => {
    e.stopPropagation();
    setIsExpanded(false);
    handleRemoveHighlights({ removeTextSelection: true });
  };

  const handleReplace = async () => {
    setIsLoading(true);
    setTimeout(() => {
      toggleEditorUpdate();
      handleRemoveHighlights();
      onClearHighlight();
      const key = word.originalWord.toLowerCase() + " " + word.errorType;
      let offsetDif = 1;
      for (const sameWord of groupWords[key].sameWords) {
        replaceWord(
          editorRef.current,
          sameWord.offset + offsetDif,
          sameWord.offset + offsetDif + sameWord.deleteCount,
          sameWord.replacement
        );
        offsetDif += groupWords[key].difOffset;
      }
      onRemove(groupWords[key].sameWords, false);
      webSocket(editorRef.current.getText());
      toggleEditorUpdate();
      setIsLoading(false);
    }, 3000); // 2-second delay
  };

  const handleReject = async () => {
    setIsLoading(true);
    setTimeout(() => {
      handleRemoveHighlights();
      onClearHighlight();
      const key = word.originalWord.toLowerCase() + " " + word.errorType;
      for (const sameWord of groupWords[key].sameWords) {
        rejectSuggestion(
          editorRef.current,
          sameWord.offset + 1,
          sameWord.offset + 1 + sameWord.deleteCount
        );
      }
      onRemove(groupWords[key].sameWords, true);
      setIsLoading(false);
    }, 1000); // 2-second delay
  };


  return (
    <div
      className={`card ${isExpanded ? "expanded" : ""} ${
        word.errorType === "spelling" ? "card-spelling" : "card-capitalization"
      }`}
      data-testid={`${
        word.errorType === "spelling" ? "card-spelling" : "card-capitalization"
      }`}
      onClick={expandCard}
    >
      <div className="card-header">
        <p className="card-title">
          {word.errorType === "spelling" ? (
            <strong data-testid="spelling-error">{t("spelling_error")}</strong>
          ) : (
            <strong data-testid="capitalization-error">{t("capitalization_error")}</strong>
          )}
          {!isExpanded && (
            <span className="word"> &#8594; {word.originalWord}</span>
          )}
        </p>
        {isExpanded && (
          <FiMinimize2
            className="minimize-icon"
            data-testid="minimize-icon"
            onClick={minimizeCard}
          />
        )}
      </div>
      {isExpanded && (
        <div className="card-content" data-testid="card-content">
          <div className="word-suggestion">
            <p className="original-word">
              {word.originalWord}{" "}
              {word.replacement !== "" && (
                <span
                  className={`corrected-word ${word.errorType === "spelling" ? "spelling-replacement" : "capitalization-replacement"}`}
                >
                  {" "}
                  &#8594; {word.replacement}
                </span>
              )}
              {word.replacement === "" && (
                <p className="not-found" data-testid="no-replacement">{t("no_replacement")}</p>
              )}
            </p>
          </div>
          {word.replacement !== "" ? (
            <div className="card-footer">
              <a
                className="reference"
                data-testid="kbbi-link"
                href={`https://kbbi.kemdikbud.go.id/entri/${
                  word.replacement.includes(" ") ? (
                      // istanbul ignore next
                      word.replacement.split(" ")[1])
                    : (word.replacement)
                }`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t("kbbi_reference")}
              </a>
              <div className="accept-decline">
              <button data-testid="accept-button" onClick={handleReplace} disabled={isLoading}>
                {isLoading ? <FaSpinner className="loading-icon" /> : <BsFillCheckCircleFill className="accept" />}
              </button>
              <button data-testid="decline-button" onClick={handleReject} disabled={isLoading}>
                {isLoading ? <FaSpinner className="loading-icon" /> : <BsXCircleFill className="decline" />}
              </button>
              </div> 
            </div>
          ) : (
            <div className="delete-keep">
              <button
                onClick={handleReplace}
                className="delete"
                data-testid="accept-button"
              >
                {t("delete_word")}
              </button>
              <button
                onClick={handleReject}
                className="keep"
                data-testid="decline-button"
              >
                {t("keep_word")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const wordShape = {
  errorType: PropTypes.string.isRequired,
  originalWord: PropTypes.string.isRequired,
  replacement: PropTypes.string.isRequired,
  offset: PropTypes.number.isRequired,
  deleteCount: PropTypes.number.isRequired,
};

SuggestionCard.propTypes = {
  word: PropTypes.shape(wordShape),
  editorRef: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  groupWords: PropTypes.arrayOf(
    PropTypes.shape({
      sameWords: PropTypes.arrayOf(PropTypes.shape(wordShape)),
      difOffset: PropTypes.number.isRequired,
    })
  ),
  highlighted: PropTypes.shape(wordShape),
  onCardClick: PropTypes.func.isRequired,
  onClearHighlight: PropTypes.func.isRequired,
  webSocket: PropTypes.func.isRequired,
};

export default SuggestionCard;
